import { InputGroup, Text, VisuallyHidden } from '@chakra-ui/react';
import { Input } from 'modules/common/components/Input/Input';
import { ChangeEvent, KeyboardEvent, ReactElement, memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DOCUMENT_TITLE_ID = 'document-title';

interface TitleInputProps {
  title: string;
  onSubmit: Function;
}

export const TitleInput = memo(({ title, onSubmit }: TitleInputProps): ReactElement => {
  const { t } = useTranslation('common_navbar');
  const [documentTitle, setDocumentTitle] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (title) {
      setDocumentTitle(title);
    }
  }, [title]);

  /**
   * Trigger title update when hitting Enter
   */
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const key = e.key || e.code;

    if (key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  /**
   * Submit the title.
   * Triggered when hitting Enter key or on blur.
   */
  const handleOnSubmit = () => {
    if (!documentTitle) {
      setDocumentTitle(title);
      return;
    }

    onSubmit(documentTitle);
  };

  /**
   * Triggered on input change
   */
  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDocumentTitle(e.target.value || '');
  };

  return (
    <InputGroup>
      <VisuallyHidden>
        <Text color={'font.300'} fontWeight={'normal'} fontSize={'8px'} as={'label'} htmlFor={DOCUMENT_TITLE_ID}>
          {t('navbar.document-title-label')}
        </Text>
      </VisuallyHidden>
      <Input
        id={DOCUMENT_TITLE_ID}
        value={documentTitle}
        onBlur={handleOnSubmit}
        onChange={onInputChange}
        variant={'outline'}
        size={'sm'}
        placeholder={t('navbar.document-title')}
        ref={inputRef}
        onKeyDown={onKeyDown}
        data-testid={'infographtitle-input'}
        textOverflow={'ellipsis'}
        w={'64'}
        p={2}
        color={'font.500'}
        borderColor={'gray.500'}
        borderRadius={'base'}
        aria-label={t('navbar.document-title-label')}
      />
    </InputGroup>
  );
});

TitleInput.displayName = 'TitleInput';
